import React, { useEffect, useRef, useState } from 'react'

import type { DragEndEvent } from '@dnd-kit/core'
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { ButtonClean, Icon } from '@fourthwall/components'
import { PencilIcon } from '@fourthwall/icons'

import type { SectionSetting } from '../../../types'
import { EditableSettingsItem } from './EditableSettingsItem'
import { InlineSettingEditor } from './InlineSettingEditor'
import * as S from './SettingsTab.styled'

type SettingsConfigurationProps = {
  settings: SectionSetting[]
  onSettingsChange: (settings: SectionSetting[]) => void
}

type SortableSettingsItemProps = {
  setting: SectionSetting
  onEdit: (setting: SectionSetting) => void
  onDelete: (settingId: string) => void
}

const SortableSettingsItem = ({ setting, onEdit, onDelete }: SortableSettingsItemProps) => {
  return <EditableSettingsItem setting={setting} onEdit={onEdit} onDelete={onDelete} />
}

export const SettingsTab = ({ settings, onSettingsChange }: SettingsConfigurationProps) => {
  const [showInlineEditor, setShowInlineEditor] = useState(false)
  const inlineEditorRef = useRef<HTMLDivElement>(null)

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const handleAddSetting = () => {
    setShowInlineEditor(true)
  }

  // Scroll to the inline editor when it becomes visible
  useEffect(() => {
    if (showInlineEditor && inlineEditorRef.current) {
      inlineEditorRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      })
    }
  }, [showInlineEditor])

  const handleEditSetting = (setting: SectionSetting) => {
    const updatedSettings = settings.map((s) => (s.id === setting.id ? setting : s))
    onSettingsChange(updatedSettings)
  }

  const handleDeleteSetting = (settingId: string) => {
    const updatedSettings = settings.filter((s) => s.id !== settingId)
    onSettingsChange(updatedSettings)
  }

  const handleSaveSetting = (setting: SectionSetting) => {
    // Add new setting
    onSettingsChange([...settings, setting])
    setShowInlineEditor(false)
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (active.id !== over?.id) {
      const oldIndex = settings.findIndex((s) => s.id === active.id)
      const newIndex = settings.findIndex((s) => s.id === over?.id)

      if (oldIndex !== -1 && newIndex !== -1) {
        const updatedSettings = [...settings]
        const [movedSetting] = updatedSettings.splice(oldIndex, 1)
        updatedSettings.splice(newIndex, 0, movedSetting)
        onSettingsChange(updatedSettings)
      }
    }
  }

  return (
    <div>
      <S.SettingsSection>
        {settings.length > 0 || showInlineEditor ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={settings.map((s) => s.id)}
              strategy={verticalListSortingStrategy}
            >
              <S.SettingsList>
                {settings.map((setting) => (
                  <SortableSettingsItem
                    key={setting.id}
                    setting={setting}
                    onEdit={handleEditSetting}
                    onDelete={handleDeleteSetting}
                  />
                ))}
              </S.SettingsList>
            </SortableContext>
          </DndContext>
        ) : (
          <S.EmptySettings>
            <S.EmptySettingsText>No settings configured</S.EmptySettingsText>
            <S.EmptySettingsSubtext>
              <ButtonClean label="Add Settings" appearance="primary" onClick={handleAddSetting} />
            </S.EmptySettingsSubtext>
          </S.EmptySettings>
        )}

        {/* Inline Editor */}
        {showInlineEditor && (
          <S.InlineEditorContainer ref={inlineEditorRef}>
            <InlineSettingEditor
              icon={<Icon component={PencilIcon} height={12} />}
              header={
                <S.SettingInfo>
                  <S.SettingType>New Setting</S.SettingType>
                  <S.SettingName>New Setting</S.SettingName>
                </S.SettingInfo>
              }
              onClose={() => setShowInlineEditor(false)}
              onSave={handleSaveSetting}
              create
            />
          </S.InlineEditorContainer>
        )}
      </S.SettingsSection>
      {settings.length > 0 && !showInlineEditor && (
        <S.AddButtonContainer>
          <ButtonClean label="Add Setting" appearance="primary" onClick={handleAddSetting} />
        </S.AddButtonContainer>
      )}
    </div>
  )
}
